import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { LlmIntegrationService } from 'src/app/core/services/llm-integration.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { FormalizeCompany, FormalizePayload, FormalizeUser } from 'src/app/core/models/formalizePayload';

@Component({
  selector: 'app-formalize-comment',
  templateUrl: './formalize-comment.component.html',
  styleUrls: ['./formalize-comment.component.css']
})
export class FormalizeCommentComponent implements OnInit {

  @Input() inputText = '';
  // Email mode: when true, component will accept `emailSubject` and `emailBody` inputs
  @Input() emailMode: boolean = false;
  @Input() emailSubject: string = '';
  @Input() emailBody: string = '';

  @Input() noteId: string = null;
  @Input() sourceScreen: string = '';
  @Input() candidate: any = null;
  @Input() requisition: any = null;
  @Input() jobMatch: any = null;
  @Input() company: any = null;

  @Output() outputText = new EventEmitter<string>();
  @Output() outputEmail = new EventEmitter<{ subject?: string; body?: string; isHtml?: boolean }>();
  tone = 'Formal';
  includeSubjectForEmail = true;
  formalizedOutput = '';
  isLoading = false;
  isSuccess = false;
  isError = false;
  successMessage = '';
  errorMessage = '';
  showVariants = false;
  variants: { tone: string; text: string }[] = [];
  isExpanded = false;
  private requestStartTime: Date = null;

  constructor(private gpt: LlmIntegrationService, private authService: AuthService) { }

  ngOnInit() {
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }

  // Formalize email subject/body when in emailMode
  formalizeEmail() {
    if (this.emailMode === false || this.isLoading) return;

    const includeSubject = this.includeSubjectForEmail && !!this.emailSubject && this.emailSubject.trim().length > 0;
    const subjectText = (this.emailSubject || '').trim();
    const bodyHtml = (this.emailBody || '').toString();

    // Extract candidate-info spans and replace with stable placeholder tokens.
    // This ensures GPT only rewrites free text — not structured name:value field data.
    const candidateTokenMap: { token: string; html: string }[] = [];
    let tokenIndex = 0;
    let tokenizedHtml = bodyHtml;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(bodyHtml, 'text/html');
      const candidateSpans = Array.from(doc.querySelectorAll('span[id^="candidate-"]'));
      candidateSpans.forEach(span => {
        const token = `__CAND_${tokenIndex++}__`;
        candidateTokenMap.push({ token, html: (span as Element).outerHTML });
        const textNode = doc.createTextNode(token);
        let sibling = span.nextSibling;
        while (sibling) {
          const el = sibling as Element;
          const tag = (sibling.nodeType === 1) ? (el.tagName || '').toUpperCase() : '';
          if (tag === 'SPAN' && el.innerHTML === '&nbsp;') {
            const toRemove = sibling;
            sibling = sibling.nextSibling;
            toRemove.parentNode.removeChild(toRemove);
          } else {
            break;
          }
        }
        span.parentNode.replaceChild(textNode, span);
      });
      tokenizedHtml = doc.body.innerHTML;
    } catch (e) {
      // Fallback: proceed with original HTML if DOM parsing fails
      tokenizedHtml = bodyHtml;
    }

    // Strip remaining HTML tags to get plain text (placeholder tokens remain).
    let bodyText = tokenizedHtml.replace(/<[^>]+>/g, '\n').replace(/\n{2,}/g, '\n').trim();

    // Separate trailing signature from main body.
    const splitSignature = (text: string) => {
      if (!text) return { main: '', signature: '' };
      const sigPattern = /(\n|^)\s*((best regards|best|regards|thanks|thank you|cheers|sincerely|warm regards|kind regards|yours sincerely|yours truly|thanks,|thank you,|--)[\.,!]?)([\s\S]*)$/i;
      const lines = text.split(/\n/);
      for (let i = Math.max(0, lines.length - 6); i < lines.length; i++) {
        const line = lines[i].trim().toLowerCase();
        if (line === '--' || /^(best regards|best|regards|thanks|thank you|cheers|sincerely|warm regards|kind regards|yours sincerely|yours truly)[\.,!]?$/i.test(line)) {
          return { main: lines.slice(0, i).join('\n').trim(), signature: lines.slice(i).join('\n').trim() };
        }
      }
      const m = text.match(sigPattern);
      if (m && m.index !== undefined) {
        const idx = text.toLowerCase().lastIndexOf(m[2].toLowerCase());
        if (idx !== -1) {
          return { main: text.slice(0, idx).trim(), signature: text.slice(idx).trim() };
        }
      }
      return { main: text.trim(), signature: '' };
    };

    const split = splitSignature(bodyText);
    const mainBody = split.main;

    if (!includeSubject && !mainBody) return;

    this.isLoading = true;
    this.isSuccess = false;
    this.isError = false;
    this.errorMessage = '';

    const parts: string[] = [];
    if (includeSubject) parts.push(`Subject: ${subjectText}`);
    parts.push(`Body:\n${mainBody}`);

    const extraInstruction = includeSubject ? '' : 'Do NOT return a "subject" key in the output.';
    // If there are candidate tokens, instruct the model to preserve them exactly.
    const tokenInstruction = candidateTokenMap.length > 0
      ? ` The body contains special placeholder tokens matching the pattern __CAND_N__ (e.g. __CAND_0__, __CAND_1__). These represent candidate field data (name:value pairs). You MUST keep every such token EXACTLY as-is — do not modify, translate, reorder, or omit them.`
      : '';

    const prompt = `You are an assistant that rewrites email subject and/or body into a single best "Recommended" variation using the ${this.tone} tone.
Do NOT add or change any sign-offs such as "Thank you", "Best regards", "Sincerely", etc. Do NOT append or modify any signature — only rewrite the core body content provided.${tokenInstruction} ${extraInstruction}
Return a JSON object with keys you changed. Example: {"subject": "...","body":"..."}. Only return valid JSON or plain text for the body/subject.\n\nInput:\n${parts.join('\n\n')}`;
    const options: any = { n: 1 };

    this.gpt.sendRequest(prompt, undefined, options,
      (res: any) => {
        let raw = '';
        if (res && res.choices && res.choices[0] && res.choices[0].message && res.choices[0].message.content) {
          raw = res.choices[0].message.content;
        } else if (res && res.choices && res.choices[0] && (res.choices[0] as any).text) {
          raw = (res.choices[0] as any).text;
        } else {
          raw = '';
        }
        raw = this._normalizeQuotes(raw);

        let parsed: any = null;
        try { parsed = JSON.parse(raw.trim()); } catch (e) {
          const start = raw.indexOf('{');
          const end = raw.lastIndexOf('}');
          if (start !== -1 && end !== -1 && end > start) {
            const jsonStr = raw.substring(start, end + 1);
            try { parsed = JSON.parse(jsonStr); } catch (e2) { parsed = null; }
          }
        }

        const out: { subject?: string; body?: string; isHtml?: boolean } = {};
        if (parsed) {
          if (includeSubject && parsed.subject) out.subject = parsed.subject.toString().trim();
          if (parsed.body) {
            // Reconstruct HTML: wrap paragraphs in <p> tags and restore candidate span tokens.
            out.body = this._buildEmailHtmlBody(parsed.body.toString().trim(), candidateTokenMap);
            out.isHtml = true;
          }
        } else if (raw.trim().length > 0) {
          // Fallback: treat whole response as rewritten body.
          out.body = this._buildEmailHtmlBody(raw.trim(), candidateTokenMap);
          out.isHtml = true;
        }

        this.isLoading = false;
        if (Object.keys(out).length > 0) {
          this.outputEmail.emit(out);
          this.isSuccess = true;
          setTimeout(() => { this.isSuccess = false; }, 2000);
        } else {
          this.isError = true;
          this.errorMessage = 'No output from AI';
        }
        this.saveFormalizationActivity(bodyHtml, out.body);
      },
      err => {
        console.error(err);
        this.isLoading = false;
        this.isError = true;
        this.errorMessage = 'Error generating text variants';
      }
    );
  }

  /**
   * Wraps formalized plain text in <p> tags to rebuild HTML structure, then restores
   * each candidate placeholder token back to its original span HTML.
   */
  private _buildEmailHtmlBody(text: string, tokenMap: { token: string; html: string }[]): string {
    let normalized = text.replace(/\\n/g, '\n');

    normalized = normalized.replace(/(__CAND_\d+__)/g, '\n$1\n');

    const tokenLookup: Record<string, string> = {};
    tokenMap.forEach(({ token, html }) => { tokenLookup[token] = html; });
    const tokenOnlyPattern = /^__CAND_\d+__$/;

    const parts = normalized
      .split(/\n+/)
      .map(line => line.trim())
      .filter(line => line.length > 0 && !/^(&nbsp;|&#160;|\u00a0)+$/i.test(line))
      .map(line => {
        if (tokenOnlyPattern.test(line)) {
          const spanHtml = tokenLookup[line];
          return spanHtml ? `${spanHtml}<span>&nbsp;</span><br>` : line;
        }
        let restored = line;
        tokenMap.forEach(({ token, html: spanHtml }) => {
          restored = restored.split(token).join(spanHtml);
        });
        return `<p>${restored}</p>`;
      });

    return parts.join('');
  }

  formalize() {
    if (!this.inputText || !this.inputText.trim() || this.isLoading) {
      return;
    }

    // Tokenize name:value pairs so GPT preserves them unchanged.
    // Candidate info is inserted into notes as "FieldName: Value" (title-case label + colon).
    const nvTokenMap: { token: string; original: string }[] = [];
    let nvIndex = 0;
    const tokenizedInput = this.inputText.replace(
      /\b([A-Z][a-zA-Z\/]*(?:\s+[A-Z][a-zA-Z\/]*)*):\s*([^,\n]+)/g,
      (match) => {
        const token = `__NV_${nvIndex++}__`;
        nvTokenMap.push({ token, original: match.trim() });
        return token;
      }
    );

    const nvInstruction = nvTokenMap.length > 0
      ? ` The text contains special placeholder tokens matching the pattern __NV_N__ (e.g. __NV_0__, __NV_1__). These represent candidate field data (name:value pairs). You MUST keep every such token EXACTLY as-is — do not modify, translate, reorder, or omit them.`
      : '';

    this.isLoading = true;
    this.isSuccess = false;
    this.isError = false;
    this.successMessage = '';
    this.errorMessage = '';
    this.showVariants = false;
    this.variants = [];
    this.requestStartTime = new Date();

    // Single API call requesting only the single best "Recommended" variation
    const prompt = `You are an assistant that rewrites the provided text into a single, best "Recommended" variation using the ${this.tone} tone suitable for UI display.${nvInstruction}\n\nReturn either:\n1) A JSON object with a single key "recommended" whose value is an object with fields "label" and "text" (label should be "Recommended"), OR\n2) Plain text containing the rewritten recommended output only.\n\nReturn only the JSON or the plain rewritten text and nothing else.\n\nText: ${nvTokenMap.length > 0 ? tokenizedInput : this.inputText}`;
    const options: any = { n: 1 };

    this.gpt.sendRequest(prompt, undefined, options, 
      (res: any) => {
        let raw = '';
        if (res && res.choices && res.choices[0] && res.choices[0].message && res.choices[0].message.content) {
          raw = res.choices[0].message.content;
        } else if (res && res.choices && res.choices[0] && (res.choices[0] as any).text) {
          raw = (res.choices[0] as any).text;
        } else {
          raw = '';
        }
        raw = this._normalizeQuotes(raw);

        let parsed: any = null;
        try {
          parsed = JSON.parse(raw.trim());
        } catch (e) {
          // try to extract JSON substring
          const start = raw.indexOf('{');
          const end = raw.lastIndexOf('}');
          if (start !== -1 && end !== -1 && end > start) {
            const jsonStr = raw.substring(start, end + 1);
            try { parsed = JSON.parse(jsonStr); } catch (e2) { parsed = null; }
          }
        }

        let variantsOut: { tone: string; text: string }[] = [];
        if (parsed && parsed.recommended) {
          const rec = parsed.recommended;
          variantsOut = [{ tone: rec.label || 'Recommended', text: (rec.text || rec.output || '').trim() }];
        } else if (parsed && parsed.variants && Array.isArray(parsed.variants)) {
          variantsOut = parsed.variants.slice(0,3).map((v: any) => ({ tone: v.label || v.tone || 'Variant', text: (v.text || v.output || '').trim() }));
        } else {
          // Try to split by labels in plain text
          const labels = ['Recommended', 'Concise', 'Detailed'];
          const found: { tone: string; text: string }[] = [];
          const lower = raw;
          for (let i = 0; i < labels.length; i++) {
            const lab = labels[i];
            const re = new RegExp(lab + '[\s:\-]*', 'i');
            const m = re.exec(lower);
            if (m && m.index !== undefined) {
              const start = m.index + m[0].length;
              let endPos = lower.length;
              for (let j = i + 1; j < labels.length; j++) {
                const r2 = new RegExp(labels[j] + '[\s:\-]*', 'i');
                const m2 = r2.exec(lower.substring(start));
                if (m2 && m2.index !== undefined) {
                  endPos = start + m2.index;
                  break;
                }
              }
              const txt = lower.substring(start, endPos).trim().replace(/^[-:\n\s]+|[-:\n\s]+$/g, '');
              if (txt.length > 0) found.push({ tone: lab, text: txt });
            }
          }
          if (found.length > 0) {
            variantsOut = found;
          } else if (raw.trim().length > 0) {
            // Take full response as Recommended
            variantsOut = [{ tone: 'Recommended', text: raw.trim() }];
          }
        }

        this.isLoading = false;
        // If we have at least one variant, auto-select the recommended (or first) variation
        if (variantsOut && variantsOut.length > 0) {
          const recommended = variantsOut.find(v => (v.tone || '').toString().toLowerCase().indexOf('recommend') !== -1) || variantsOut[0];
          // Restore any name:value tokens back to their original text.
          if (nvTokenMap.length > 0) {
            recommended.text = nvTokenMap.reduce(
              (acc, { token, original }) => acc.split(token).join(original),
              recommended.text
            );
          }
          this.selectVariant(recommended);
          this.saveFormalizationActivity(this.inputText, recommended.text);
        } else {
          this.showVariants = false;
        }
      },
      err => {
        console.error(err);
        this.isLoading = false;
        this.showVariants = false;
        this.isSuccess = false;
        this.successMessage = '';
        this.isError = true;
        this.errorMessage = 'Error generating text variants';
      }
    );
  }

  private generateVariant(toneVal: string, style: string): Promise<{ tone: string; text: string }> {
    return new Promise((resolve) => {
      let styleInstructions = '';
      if (style === 'concise') {
        styleInstructions = ' Keep it concise and to the point.';
      } else if (style === 'detailed') {
        styleInstructions = ' You may add a bit more detail or context if needed.';
      }

      const prompt = `You are an assistant that rewrites text. Produce exactly one output only — do not return multiple variants, lists, or commentary.\n\nRewrite the following text in a ${toneVal} tone suitable for display in a UI.${styleInstructions} Return only the rewritten text as a single output.\n\nText: ${this.inputText}`;
      const options: any = { n: 1 };

      this.gpt.sendRequest(prompt, undefined, options, 
        (res: any) => {
          let raw = '';
          if (res && res.choices && res.choices[0] && res.choices[0].message && res.choices[0].message.content) {
            raw = res.choices[0].message.content;
          } else if (res && res.choices && res.choices[0] && (res.choices[0] as any).text) {
            raw = (res.choices[0] as any).text;
          } else {
            raw = '';
          }
          resolve({ tone: toneVal, text: raw.trim() });
        },
        err => {
          console.error(err);
          resolve({ tone: toneVal, text: 'Error generating variant' });
        }
      );
    });
  }

  saveFormalizationActivity(originalText: string, formalizedText: string) {
    const endTime = new Date();
    const startTime = this.requestStartTime || endTime;
    const duration = Math.round((endTime.getTime() - startTime.getTime()) / 1000);
    const userDetails = this.authService.getUserDetails();

    const payload = new FormalizePayload({
      duration: duration,
      dateRange: {
        startDate: startTime.toISOString(),
        endDate: endTime.toISOString()
      },
      sourceScreen: this.sourceScreen || null,
      company: this.company ? new FormalizeCompany(this.company.companyId, this.company.companyName, "", "") : null,
      jobMatch: this.jobMatch || null,
      requisition: this.requisition || null,
      candidate: this.candidate || null,
      user: userDetails ? new FormalizeUser(
        userDetails.id,
        userDetails.firstname,
        userDetails.lastname,
        userDetails.emailId
      ) : null,
      originalText: originalText,
      formalizedText: formalizedText,
      tone: this.tone,
      savedNote: null,
      noteId: this.noteId || null
    });

    this.gpt.saveTextFormalization(payload, (response) => {
      // Activity logged silently
    }, (error) => {
      console.error('Failed to save text formalization activity', error);
    });
  }

  /**
   * Replaces typographic/curly quotes and dashes returned by GPT with plain ASCII equivalents
   * to prevent UTF-8 encoding corruption (e.g. â€™ appearing as â) in the UI.
   */
  private _normalizeQuotes(text: string): string {
    if (!text) return text;
    return text
      .replace(/[\u2018\u2019\u02BC\u0060]/g, "'")   // curly single quotes / backtick → '
      .replace(/[\u201C\u201D]/g, '"')                 // curly double quotes → "
      .replace(/[\u2013\u2014]/g, '-')                 // en/em dash → -
      .replace(/\u2026/g, '...')                       // ellipsis → ...
      .replace(/\u00A0/g, ' ');                        // non-breaking space → space
  }

  selectVariant(variant: { tone: string; text: string }) {
    this.formalizedOutput = variant.text;
    this.outputText.emit(variant.text);
    this.showVariants = false;
    this.variants = [];
    this.isSuccess = true;
    this.isError = false;
    this.errorMessage = '';
    this.successMessage = `Selected ${variant.tone} variation!`;
    setTimeout(() => {
      this.isSuccess = false;
      this.successMessage = '';
    }, 2500);
  }

}
